/**
 * Social Media Asset Creation Component
 * Handles Instagram, Facebook, LinkedIn, Twitter/X, TikTok posts
 */

import React, { useState } from 'react';
import { Instagram, Facebook, Linkedin, Twitter, MessageCircle, Upload, Eye, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface SocialAssetProps {
  clientId?: number;
  planId?: number;
  projectId?: number;
  onComplete?: (contentId: number) => void;
  onCancel?: () => void;
}

interface Platform {
  id: string;
  name: string;
  icon: React.ReactNode;
  postTypes: string[];
  maxLength: number;
  color: string;
}

const platforms: Platform[] = [
  { id: 'instagram', name: 'Instagram', icon: <Instagram className="w-5 h-5" />, postTypes: ['Post', 'Story', 'Reel', 'Carousel'], maxLength: 2200, color: 'bg-pink-500' },
  { id: 'facebook', name: 'Facebook', icon: <Facebook className="w-5 h-5" />, postTypes: ['Post', 'Story'], maxLength: 63206, color: 'bg-blue-600' },
  { id: 'linkedin', name: 'LinkedIn', icon: <Linkedin className="w-5 h-5" />, postTypes: ['Post', 'Article'], maxLength: 3000, color: 'bg-blue-700' },
  { id: 'twitter', name: 'Twitter/X', icon: <Twitter className="w-5 h-5" />, postTypes: ['Tweet', 'Thread'], maxLength: 280, color: 'bg-black' },
  { id: 'tiktok', name: 'TikTok', icon: <MessageCircle className="w-5 h-5" />, postTypes: ['Video'], maxLength: 2200, color: 'bg-gray-900' },
];

export default function SocialAssetCreation({ clientId, planId, projectId, onComplete, onCancel }: SocialAssetProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [postType, setPostType] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [media, setMedia] = useState<File[]>([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    callToAction: '',
    linkUrl: '',
    location: '',
    taggedAccounts: '',
  });

  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setMedia(Array.from(e.target.files));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // Step 1: Create content item
      const contentPayload = {
        type: 'social',
        content_subtype: `${selectedPlatforms[0]}_${postType.toLowerCase()}`,
        title: formData.title || `${selectedPlatforms.join(', ')} Post`,
        description: caption.substring(0, 200),
        status: 'draft',
        client_id: clientId,
        plan_id: planId,
        project_id: projectId,
        platform_config: JSON.stringify({
          platforms: selectedPlatforms,
          post_type: postType,
          caption,
          hashtags,
          call_to_action: formData.callToAction,
          link_url: formData.linkUrl,
          location: formData.location,
          tagged_accounts: formData.taggedAccounts,
          scheduled_date: scheduledDate,
        }),
      };

      const contentRes = await axios.post(`${API_BASE_URL}/content`, contentPayload);
      const contentId = contentRes.data.id;

      // Step 2: Upload media assets
      if (media.length > 0) {
        for (const [index, file] of media.entries()) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('content_id', contentId.toString());
          formData.append('asset_type', file.type.startsWith('video/') ? 'video' : 'image');
          formData.append('asset_group', 'primary');
          formData.append('platform_target', selectedPlatforms.join(','));

          await axios.post(`${API_BASE_URL}/assets/upload`, formData);
        }
      }

      // Step 3: Create workflow steps
      const workflowSteps = [
        { step_order: 1, step_name: 'Platform Selection', step_type: 'metadata', status: 'completed' },
        { step_order: 2, step_name: 'Post Details', step_type: 'metadata', status: 'completed' },
        { step_order: 3, step_name: 'Content & Caption', step_type: 'content', status: 'completed' },
        { step_order: 4, step_name: 'Media Upload', step_type: 'assets', status: 'completed' },
        { step_order: 5, step_name: 'Submit for Approval', step_type: 'approval', status: 'pending' },
      ];

      for (const step of workflowSteps) {
        await axios.post(`${API_BASE_URL}/content/${contentId}/workflow/steps`, step);
      }

      alert('Social media post created successfully!');
      if (onComplete) onComplete(contentId);
    } catch (error) {
      console.error('Failed to create social asset:', error);
      alert('Failed to create social media post');
    } finally {
      setLoading(false);
    }
  };

  const getCharacterLimit = () => {
    if (selectedPlatforms.length === 0) return 0;
    const platform = platforms.find(p => p.id === selectedPlatforms[0]);
    return platform?.maxLength || 0;
  };

  const getPlatformPostTypes = () => {
    if (selectedPlatforms.length === 0) return [];
    const platform = platforms.find(p => p.id === selectedPlatforms[0]);
    return platform?.postTypes || [];
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Social Media Post</h1>
              <p className="text-gray-600 mt-1">Multi-platform social content creation</p>
            </div>
            {onCancel && (
              <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            )}
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6">
            {[
              { num: 1, label: 'Platform' },
              { num: 2, label: 'Details' },
              { num: 3, label: 'Content' },
              { num: 4, label: 'Media' },
              { num: 5, label: 'Preview' },
            ].map((step, i) => (
              <React.Fragment key={step.num}>
                <div className={`flex items-center ${currentStep >= step.num ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.num ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {step.num}
                  </div>
                  <span className="ml-2 text-sm font-medium hidden sm:inline">{step.label}</span>
                </div>
                {i < 4 && <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Step 1: Platform Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Select Platform(s)</h2>
              <p className="text-sm text-gray-600">Choose one or more platforms for this post</p>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {platforms.map(platform => (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    className={`p-4 rounded-lg border-2 transition ${
                      selectedPlatforms.includes(platform.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-12 h-12 ${platform.color} rounded-lg flex items-center justify-center mx-auto mb-2 text-white`}>
                      {platform.icon}
                    </div>
                    <p className="text-sm font-medium text-center">{platform.name}</p>
                  </button>
                ))}
              </div>

              {selectedPlatforms.length > 0 && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Post Type</label>
                  <select
                    value={postType}
                    onChange={(e) => setPostType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select post type...</option>
                    {getPlatformPostTypes().map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={selectedPlatforms.length === 0 || !postType}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Next: Details
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Post Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Post Details</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Post Title (Internal)</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., December Product Launch Announcement"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Call to Action</label>
                  <input
                    type="text"
                    value={formData.callToAction}
                    onChange={(e) => setFormData({ ...formData, callToAction: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Shop Now, Learn More"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link URL (optional)</label>
                  <input
                    type="url"
                    value={formData.linkUrl}
                    onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location (optional)</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., New York, NY"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date (optional)</label>
                  <input
                    type="datetime-local"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next: Content
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Content & Caption */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Content & Caption</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={6}
                  maxLength={getCharacterLimit()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Write your post caption..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {caption.length} / {getCharacterLimit()} characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hashtags</label>
                <input
                  type="text"
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="#marketing #socialmedia #business"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tagged Accounts (optional)</label>
                <input
                  type="text"
                  value={formData.taggedAccounts}
                  onChange={(e) => setFormData({ ...formData, taggedAccounts: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="@account1 @account2"
                />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={!caption}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Media
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Media Upload */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Upload Media</h2>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label htmlFor="media-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-700 font-medium">Click to upload</span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">Images or videos (max 10MB each)</p>
                  <input
                    id="media-upload"
                    type="file"
                    onChange={handleMediaUpload}
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                  />
                </div>

                {media.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {media.map((file, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(5)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  Preview
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Preview & Submit */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Preview & Submit</h2>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                    <div className="ml-3">
                      <p className="font-semibold text-sm">Your Account</p>
                      <p className="text-xs text-gray-500">{selectedPlatforms.map(p => platforms.find(pl => pl.id === p)?.name).join(', ')}</p>
                    </div>
                  </div>

                  {media.length > 0 && (
                    <div className="mb-3 bg-gray-200 h-64 rounded-lg flex items-center justify-center">
                      <p className="text-gray-500 text-sm">{media.length} media file(s)</p>
                    </div>
                  )}

                  <p className="text-sm mb-2">{caption}</p>
                  {hashtags && <p className="text-sm text-blue-600">{hashtags}</p>}
                  {formData.location && (
                    <p className="text-xs text-gray-500 mt-2">📍 {formData.location}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
